import React, { useEffect, useState } from "react";
import { Button, Card, Typography } from "@material-ui/core";
import { useCookies } from "react-cookie";
import { ToastContainer } from "react-toastify";
import { DarkGrey } from "../../shared/styles/colors";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import ApiService from "../../service/ApiService";
import AlertService from "../../service/AlertService";
import Pathnames from "../../model/enum/Pathnames";
import Spinner from "../../shared/components/loader/Spinner";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ToastMessages from "../../model/enum/toast-messages.enum";
import User from "../../model/user";
import Auth from "../../model/auth";
import Session from "../../model/session";
import Logo from "../../assets/main.png";
import useStyles from "./styles";
import Misc from "../../utils/Misc";
import Permission from "../../model/permission";
import "./Login.scss";
import EmailService from "../../service/EmailService";

const Login = () => {
  const classes = useStyles();
  const [cookies, setCookie] = useCookies(["session"]);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [errorUsernameOrEmail, setErrorUsernameOrEmail] = useState("");
  const [errorPwd, setErrorPwd] = useState("");
  const [viewPassword, setViewPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const login = async () => {
    //Misc.validPasswordFormat(password)
    let toggleUsername = email === "" || email.indexOf("@") === -1;

    if (
      (toggleUsername || Misc.validEmailFormat(email)) &&
      !Misc.isNullOrEmpty(password)
    ) {
      setLoading(true);
      setErrorUsernameOrEmail("");
      setErrorPwd("");
      try {
        const payload: Auth = {
          email: email,
          password: password,
        };

        let session: Session;

        if (toggleUsername) {
          delete payload.email;
          payload.username = username;

          session = await ApiService.post<Session>(
            `${DatabaseEntities.AUTH}/loginWithUsername`,
            payload,
          );
        } else {
          session = await ApiService.post<Session>(
            `${DatabaseEntities.AUTH}/loginWithEmail`,
            payload,
          );
        }

        if (session) {
          setCookie("session", session);
          localStorage.setItem("accessToken", session.accessToken);
          localStorage.setItem("userEmail", email);

          const user: User = await ApiService.get<User>(
            `${DatabaseEntities.USERS}/${session.userId}`,
            session.accessToken,
          );

          localStorage.setItem("userId", user.id.toString());
          localStorage.setItem("userName", user.username);
          localStorage.setItem("userRole", user?.Role?.name ?? "");
          localStorage.setItem("userRoleId", user.Role?.id?.toString() ?? "");

          const permissions = await ApiService.get<Permission[]>(
            `${DatabaseEntities.ROLES}/${user.Role?.id}/permissions`,
            session.accessToken,
          );

          localStorage.setItem(
            "permissions",
            JSON.stringify(permissions ?? []),
          );

          AlertService.success(ToastMessages.LOGGED_IN);

          // Send login attempt email
          await EmailService.sendEmail(
            user.firstName,
            email,
            "Login Attempt",
            new Date(),
          );

          setInterval(() => {
            if (!user.activated) {
              window.open(
                Pathnames.ACTIVATE_ACCOUNT +
                  "?userId=" +
                  user.id +
                  "&email=" +
                  email,
                "_self",
              );
            } else {
              window.open(Pathnames.DASHBOARD, "_self");
            }
          }, 2000);
        } else {
          AlertService.error(ToastMessages.LOGIN_FAILED);
        }
      } catch (error: any) {
        AlertService.error(`${ToastMessages.GENERIC_ERROR}: ${error.message}`);
      } finally {
        setLoading(false);
      }
    } else {
      AlertService.warning(ToastMessages.INVALID_LOGIN_FORM);
    }
  };

  useEffect(() => {
    const keyDownHandler = (event: {
      key: string;
      preventDefault: () => void;
    }) => {
      if (event.key === "Enter") {
        event.preventDefault();
        login();
      }
    };

    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  });

  const handleUsernameOrEmailChange = (e: any) => {
    const value = e.target.value;

    if (value === "") setErrorUsernameOrEmail("Username or email is required");
    else setErrorUsernameOrEmail("");

    setEmail(value);
    setUsername(value);
  };

  const handlePwdChange = (e: any) => {
    const value = e.target.value;
    if (value === "") setErrorPwd("");
    else setPassword(value);
  };

  return (
    <div className={classes.login}>
      <Card className={classes.loginCard}>
        <img src={Logo} className={classes.logo} alt="logo" />
        <div className="form__group">
          <input
            type="text"
            className={classes.input}
            placeholder="Username or email"
            name="usernameOrEmail"
            id="usernameOrEmail"
            required
            value={email || username}
            onChange={(e) => {
              handleUsernameOrEmailChange(e);
            }}
          />
          <label className="form__label">Username or Email</label>
        </div>
        <label className={classes.errorText}>{errorUsernameOrEmail}</label>
        <div className="form__group">
          <div className={classes.inputDiv}>
            <input
              type={viewPassword ? "text" : "password"}
              className={classes.emptyInput}
              placeholder="**********"
              name="password"
              id="password"
              required
              value={password}
              onChange={handlePwdChange}
            />
            <label className="form__label">Password</label>
            {viewPassword ? (
              <Button onClick={() => setViewPassword(!viewPassword)}>
                <VisibilityOffIcon />
              </Button>
            ) : (
              <Button onClick={() => setViewPassword(!viewPassword)}>
                <VisibilityIcon />
              </Button>
            )}
          </div>
        </div>
        <label className={classes.errorText}>{errorPwd}</label>
        <button className={classes.button} onClick={login} disabled={loading}>
          Login
        </button>
        {error ? <p className={classes.errorText}>{error}</p> : null}
        {loading && <Spinner />}
        <hr style={{ width: "20%" }} />
        <div className={classes.checkboxDiv}>
          <Typography style={{ color: DarkGrey, fontSize: ".9rem" }}>
            V1.4.4
          </Typography>
        </div>
      </Card>
      <ToastContainer />
    </div>
  );
};

export default Login;
